import React, { useState } from "react";
import { Button, Table, TextInput } from "flowbite-react";
import { HiPencil, HiPlus, HiSearch } from "react-icons/hi";
import { SubCategory } from "../../Models/SubCategory";
import { Category } from "../../Models/Category";
import EditSubCategoryModal from "./EditSubCategoryModal";

interface SubCategoryListTableProps {
  data: SubCategory[];
  categories: Category[];
  onRefresh: () => Promise<void> | undefined;
}

export default function SubCategoryListTable({
  data,
  categories,
  onRefresh,
}: SubCategoryListTableProps) {
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [selectedSubCategory, setSelectedSubCategory] =
    useState<SubCategory | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const getCategoryName = (subCategory: SubCategory) => {
    if (subCategory.category) {
      return subCategory.category.name;
    }
    if (!subCategory.categoryId) return "N/A";
    const category = categories.find(
      (c) => c.number === subCategory.categoryId
    );
    return category ? category.name : "Unknown";
  };

  // Filter data based on search term
  const filteredData = data.filter((subCategory) => {
    const categoryName = getCategoryName(subCategory);
    return (
      subCategory.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subCategory.number?.toString().includes(searchTerm) ||
      categoryName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Group subcategories by category, then sort by number within each category
  const groupedSubCategories = filteredData.reduce((groups, subCategory) => {
    const categoryName = getCategoryName(subCategory);
    if (!groups[categoryName]) {
      groups[categoryName] = [];
    }
    groups[categoryName].push(subCategory);
    return groups;
  }, {} as Record<string, SubCategory[]>);

  // Sort each group by number and sort category names
  Object.keys(groupedSubCategories).forEach((categoryName) => {
    groupedSubCategories[categoryName].sort(
      (a, b) => (a.number || 0) - (b.number || 0)
    );
  });

  const sortedCategoryNames = Object.keys(groupedSubCategories).sort();

  const handleEdit = (subCategory: SubCategory) => {
    setSelectedSubCategory(subCategory);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedSubCategory(null);
  };

  return (
    <>
      <div className="mb-4 flex justify-between items-center">
        <div className="flex-1 max-w-md">
          <TextInput
            id="search"
            type="text"
            placeholder="Search subcategories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={HiSearch}
            sizing="md"
          />
        </div>
        <Button className="buttonMain" onClick={() => setShowEditModal(true)}>
          <HiPlus className="mr-2 h-4 w-4" />
          Add SubCategory
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <Table.Head>
            <Table.HeadCell>Number</Table.HeadCell>
            <Table.HeadCell>Name</Table.HeadCell>
            <Table.HeadCell>Category</Table.HeadCell>
            <Table.HeadCell>Actions</Table.HeadCell>
          </Table.Head>
          <Table.Body className="divide-y">
            {sortedCategoryNames.map((categoryName) => (
              <React.Fragment key={categoryName}>
                {/* Category Header Row */}
                <Table.Row className="bg-gray-50 dark:bg-gray-700">
                  <Table.Cell
                    colSpan={4}
                    className="font-semibold text-gray-900 dark:text-white"
                  >
                    {categoryName}
                  </Table.Cell>
                </Table.Row>
                {/* SubCategories for this category */}
                {groupedSubCategories[categoryName].map((subCategory) => (
                  <Table.Row
                    key={subCategory.number}
                    className="bg-white dark:border-gray-700 dark:bg-gray-800"
                  >
                    <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                      {subCategory.number}
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap text-gray-900 dark:text-white">
                      {subCategory.name}
                    </Table.Cell>
                    <Table.Cell className="whitespace-nowrap text-gray-900 dark:text-white">
                      {getCategoryName(subCategory)}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          color="blue"
                          onClick={() => handleEdit(subCategory)}
                        >
                          <HiPencil className="h-4 w-4" />
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </React.Fragment>
            ))}
          </Table.Body>
        </Table>
      </div>

      <EditSubCategoryModal
        show={showEditModal}
        setShow={handleCloseEditModal}
        subCategory={selectedSubCategory}
        categories={categories}
        onRefresh={onRefresh}
      />
    </>
  );
}
